/**
 * Design tokens for z-index, shadows, transitions, and spacing.
 * Use these instead of magic numbers throughout the codebase.
 */

export const zIndex = {
  /** Relative overlay within a card or section (e.g. image caption) */
  localOverlay: 1,
  /** Low-level stacking for layered content (e.g. decorative elements) */
  raised: 5,
  /** Slider navigation, CTA positioning, labels */
  midLayer: 500,
  /** Content block text overlay (brand blocks, magazine blocks, multi-image blocks) */
  contentOverlay: 555,
  /** Lease preferences card */
  cardOverlay: 750,
  /** Sticky header / navigation */
  header: 900,
  /** Header logo on mobile — above header chrome */
  headerLogo: 950,
  /** Mega menu dropdown — above header, below search panel */
  megaMenu: 980,
  /** Lease builder overlays (image slider controls, price badges) */
  leaseOverlay: 1000,
  /** Search panel (just below MUI modals) */
  searchPanel: 1250,
  /** MUI Modals / dialogs (MUI default is 1300) */
  modal: 1300,
  /** Cookie consent banner — below dialogs, above page/search chrome */
  cookieConsent: 1290,
  /** Floating contact launcher — below dialogs and cookie consent */
  floatingMenu: 1280,
  /** Skip-to-content link */
  skipLink: 9999,
  /** Loading backdrop — above everything except skip link */
  loadingBackdrop: 10000,
  /** Screen reader focus overlay */
  screenReaderFocus: 100000,
  /** Toast notifications */
  toast: 200000,
} as const;

export const shadow = {
  /** Subtle card shadow */
  card: '0 5px 20px 0 rgba(0, 0, 0, 0.1)',
  /** Soft card shadow (lighter variant for image sliders) */
  softCard: '0 8px 20px rgba(0, 0, 0, 0.05)',
  /** Very subtle shadow (contact form, minimal elevation) */
  subtle: '0 16px 30px rgba(24, 24, 24, 0.05)',
  /** Elevated panel (cookie banner, floating menu) */
  elevated: '0 18px 44px rgba(0, 0, 0, 0.2)',
  /** Search/dropdown panel shadow */
  panel: '0 18px 36px rgba(0, 0, 0, 0.25)',
  /** Modal/dialog shadow */
  dialog: '0 26px 64px rgba(15, 15, 15, 0.22)',
  /** Floating menu launcher */
  launcher: '0 14px 34px rgba(14, 14, 14, 0.28)',
  /** Floating menu launcher hover */
  launcherHover: '0 18px 38px rgba(14, 14, 14, 0.34)',
  /** Review source badge */
  badge: '0 6px 16px rgba(0, 0, 0, 0.12)',
  /** Whisper shadow for cards on white bg (resting state) */
  whisper: '0 4px 14px rgba(0, 0, 0, 0.03)',
  /** Card hover shadow */
  cardHover: '0 8px 20px rgba(0, 0, 0, 0.08)',
  /** Dark surface card shadow */
  darkCard: '0 8px 22px rgba(0, 0, 0, 0.22)',
  /** Search/preferences panel shadow */
  searchPanel: '0 10px 28px rgba(0, 0, 0, 0.18)',
  /** LeaseBuilder surface shadow */
  lbSurface: '0 16px 30px rgba(24, 24, 24, 0.05)',
  /** LeaseBuilder card shadow */
  lbCard: '0 14px 28px rgba(24, 24, 24, 0.06)',
  /** LeaseBuilder elevated shadow */
  lbElevated: '0 18px 34px rgba(12, 12, 12, 0.2)',
  /** Inset highlight on image controls */
  insetHighlight: 'inset 0 1px 0 rgba(255, 255, 255, 0.75)',
  /** Hero widget panel shadow */
  heroWidget: '0 2px 10px rgba(0, 0, 0, 0.15)',
  /** Review card hover shadow */
  reviewHover: '0 8px 16px rgba(0, 0, 0, 0.14)',
  /** LeaseBuilder recommended deal card */
  lbDealCard: '0 14px 24px rgba(0, 0, 0, 0.16)',
  /** Header scrolled/sticky shadow */
  headerScrolled: '0 6px 18px rgba(0, 0, 0, 0.28)',
  /** Dark card hover shadow */
  darkCardHover: '0 10px 24px rgba(0, 0, 0, 0.28)',
  /** Floating panel (lease preferences) */
  floatingPanel: '0 -4px 16px rgba(0, 0, 0, 0.08)',
  /** LeaseBuilder recap panel — compact */
  lbRecapCompact: '0 10px 20px rgba(24, 24, 24, 0.05)',
  /** LeaseBuilder recap panel — full */
  lbRecapFull: '0 18px 34px rgba(24, 24, 24, 0.08)',
} as const;

export const transition = {
  /** Fast micro-interactions: hover state changes, icon toggles, button feedback. 160ms ease-out. */
  fast: '160ms ease-out',
  /** Standard UI transitions: menu open/close, color shifts, opacity fades, border highlights. 200ms ease-out. */
  standard: '200ms ease-out',
  /** Deliberate content transitions: accordion expand/collapse, nav transforms, text color shifts. 333ms ease-in-out. */
  slow: '333ms ease-in-out',
  /** Dramatic content reveals: image zoom on hover, block scale transitions, card entrance effects. 800ms cubic-bezier(.76,.53,.22,.86). */
  dramatic: '800ms cubic-bezier(.76,.53,.22,.86)',
} as const;

export const spacing = {
  /** Hairline gap */
  xxs: 2,
  /** Extra small gap (e.g. between icon and text) */
  xs: 4,
  /** Small gap */
  sm: 8,
  /** Medium gap */
  md: 16,
  /** Medium-large gap */
  mdLg: 20,
  /** Large gap */
  lg: 24,
  /** Large-extra-large gap */
  lgXl: 32,
  /** Extra large gap */
  xl: 40,
  /** 2x large gap */
  xxl: 48,
  /** 3x large gap */
  xxxl: 56,
  /** 4x large gap */
  xxxxl: 72,
} as const;

/**
 * Responsive page-layout roles. These values describe relationships between
 * page regions; use `spacing` for padding inside an individual component.
 */
export const layout = {
  /** Maximum width shared by primary page content and full-width sections. */
  contentMaxWidth: 1536,
  /** Inline distance between the viewport and primary page content. */
  pageGutter: {
    compact: 16,
    standard: 24,
    wide: 32,
  },
  /** Vertical distance between distinct page sections. */
  sectionRhythm: {
    compact: 32,
    standard: 40,
    wide: 56,
  },
  /** Gap between peer components within a section. */
  componentGap: {
    compact: 16,
    standard: 24,
    wide: 32,
  },
} as const;

export const border = {
  /** Light border on white backgrounds — cards, dividers */
  light: 'rgba(43, 43, 43, 0.08)',
  /** Light border on dark backgrounds */
  lightOnDark: 'rgba(255, 255, 255, 0.08)',
  /** Medium border on white backgrounds */
  medium: 'rgba(43, 43, 43, 0.15)',
  /** Medium border on dark backgrounds */
  mediumOnDark: 'rgba(255, 255, 255, 0.15)',
} as const;

export const opacity = {
  /** Barely visible tint */
  subtle: 0.08,
  /** Light overlay / border hint */
  light: 0.15,
  /** Medium overlay */
  medium: 0.3,
  /** Half transparent */
  half: 0.5,
  /** Heavy overlay */
  heavy: 0.75,
  /** Nearly opaque */
  nearOpaque: 0.9,
} as const;

/** Swiper slide transition speed in ms — premium content slider pacing. */
export const swiperSpeedMs = 420;

export const lineHeight = {
  /** Headlines, large display text */
  tight: 1.1,
  /** Subheadings, compact UI text */
  snug: 1.25,
  /** Body copy, general content */
  normal: 1.45,
  /** Long-form prose, relaxed reading */
  relaxed: 1.6,
} as const;

export const letterSpacing = {
  /** Display text optical tightening */
  tight: '-0.02em',
  /** Subheading tightening */
  snug: '-0.01em',
  /** Minimal tracking (nav sub-items) */
  subtle: 0.15,
  /** Metadata labels, search headings */
  standard: 0.5,
  /** Buttons, nav items, general uppercase */
  wide: 1,
  /** Form highlights */
  wider: 1.5,
  /** Hero eyebrows, slider nav */
  widest: 2,
  /** Overline, small uppercase (scales with font) */
  proportional: '0.08em',
  /** Enquiry/step headings */
  proportionalWide: '0.1em',
  /** Floating menu, summary labels */
  proportionalWider: '0.12em',
  /** Confirmation headings, step headers */
  proportionalWidest: '0.14em',
} as const;

export const borderRadius = {
  /** Default — sharp corners, the dominant design language */
  none: 0,
  /** Scrollbar thumbs, progress bar tracks/fills — functional rounding for tiny UI chrome */
  pill: 999,
  /** Circular elements: avatars, icon badges, decorative dots */
  circle: '50%',
} as const;

export const hoverScale = {
  /** Small UI elements (header badge) */
  subtle: 'scale(1.015)',
  /** All content image blocks (brand, magazine, news cards) */
  dramatic: 'scale(1.1)',
} as const;

/** Shared interaction contract for image-led brand and magazine tiles. */
export const editorialCardMotion = {
  transition: transition.slow,
  imageScale: hoverScale.dramatic,
  arrowTravelPx: 15,
} as const;
